// External Imports
import { Fragment, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import VehicleSelector from "./VehicleSelector";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import { formatCurrency } from "../../util/format-text";
import { MdCheck, MdClose, MdEdit, MdRefresh } from "react-icons/md";
import DetailPair from "../UI/DetailPair";

// Internal Imports

/** ========================================================================
 * * PricingPage Component
 * This component renders a form to update pricing.
 * ========================================================================== */

export default function PricingPage() {
  // Initialize States
  const [pricing, setPricing] = useState({});
  const [edit, setEdit] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ====================== FETCH PRICING ====================== */
  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "pricing",
        id: "default",
      });
      setPricing(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== SELECT VEHICLE ====================== */
  const selectVehicle = async (vehicle) => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "vehicle",
        id: vehicle.id,
        columns: [
          "applications: applications_on_vehicle { id highValue avgValue lowValue lastValueCheck monthlyPayment }",
          "make { name }",
        ],
      });
      if (response.applications.length > 0) {
        response.application = response.applications[0];
        const rates = calculateRates({ pricing, vehicle: response });
        response.newStandardRate = rates.standardRate;
        response.newStandardExtraMiles = rates.standardExtraMiles;
        response.newExtendedRate = rates.extendedRate;
        response.newExtendedExtraMiles = rates.extendedExtraMiles;
        response.newMinimumRate = rates.minimumRate;
        response.newMinimumExtraMiles = rates.minimumExtraMiles;
        setVehicles([...vehicles, response]);
      } else alert("Selected vehicle does not have an associated application.");

      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== UPDATE PRICING ====================== */
  const updatePricing = async () => {
    try {
      const newPricing = {
        apr: Number(document.getElementById("apr").value),
        term: Number(document.getElementById("term").value),
        taxes: Number(document.getElementById("taxes").value),
        depreciation: [],
        standardMiles: Number(document.getElementById("standardMiles").value),
        extendedMiles: Number(document.getElementById("extendedMiles").value),
        minimumMiles: Number(document.getElementById("minimumMiles").value),
        currentYear: Number(document.getElementById("currentYear").value),
      };
      for (let year = 1; year <= 6; year++) {
        const depValue = Number(
          document.getElementById(`depreciation_${year}`).value
        );
        newPricing.depreciation.push(depValue);
      }

      // Persist updated pricing metrics to the backend
      const payload = {
        id: pricing.id || "default",
        apr: newPricing.apr,
        term: newPricing.term,
        taxes: newPricing.taxes,
        standardMiles: newPricing.standardMiles,
        extendedMiles: newPricing.extendedMiles,
        minimumMiles: newPricing.minimumMiles,
        dpy1: newPricing.depreciation[0],
        dpy2: newPricing.depreciation[1],
        dpy3: newPricing.depreciation[2],
        dpy4: newPricing.depreciation[3],
        dpy5: newPricing.depreciation[4],
        dpy6: newPricing.depreciation[5],
      };

      setLoading(true);
      await requestAdminAPI("update-pricing-metrics", payload);

      // After successful save, refetch pricing from the server so the UI
      // (including the refresh icon) reflects the persisted values.
      await refreshPricing();
      setEdit(false);
    } catch (err) {
      alert(`Error updating pricing: ${err.message}`);
    }
  };

  /* ====================== REFRESH PRICING ====================== */
  const refreshPricing = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "pricing",
        id: "default",
      });
      setPricing(response);
      const newPricing = response;
      // Recalculate vehicle rates
      const updatedVehicles = vehicles.map((vehicle) => {
        const rates = calculateRates({ pricing: newPricing, vehicle });
        vehicle.newStandardRate = rates.standardRate;
        vehicle.newStandardExtraMiles = rates.standardExtraMiles;
        vehicle.newExtendedRate = rates.extendedRate;
        vehicle.newExtendedExtraMiles = rates.extendedExtraMiles;
        vehicle.newMinimumRate = rates.minimumRate;
        vehicle.newMinimumExtraMiles = rates.minimumExtraMiles;
        return vehicle;
      });
      setVehicles(updatedVehicles);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== CALCULATE RATES ====================== */
  const calculateRates = ({ pricing, vehicle }) => {
    const loanAmount =
      vehicle.application.highValue +
      pricing.taxes * vehicle.application.highValue;
    const monthlyApr = pricing.apr / 12;
    console.log("Monthly APR", monthlyApr);

    const basePrice =
      (loanAmount * (monthlyApr * Math.pow(1 + monthlyApr, pricing.term))) /
      (Math.pow(1 + monthlyApr, pricing.term) - 1);
    console.log("Base Price", basePrice);

    const currentYear = pricing.currentYear || new Date().getFullYear();
    const depreciationSurcharge =
      (pricing.depreciation[
        Math.min(5, Math.max(0, currentYear - vehicle.year))
      ] *
        vehicle.application.highValue) /
      12;
    console.log("Depreciation Surcharge", depreciationSurcharge);

    const standardRate = Number((basePrice + depreciationSurcharge).toFixed(2));
    const standardExtraMiles = Number(
      (
        standardRate / pricing.standardMiles -
        depreciationSurcharge / pricing.standardMiles
      ).toFixed(2)
    );
    const extendedRate = Number(
      (
        standardExtraMiles *
          (pricing.extendedMiles - pricing.standardMiles) *
          0.4 +
        standardRate
      ).toFixed(2)
    );
    const extendedExtraMiles = Number(
      (
        extendedRate / pricing.extendedMiles -
        depreciationSurcharge / pricing.extendedMiles
      ).toFixed(2)
    );
    const minimumRate = Number(
      (
        standardExtraMiles * pricing.minimumMiles +
        depreciationSurcharge
      ).toFixed(2)
    );
    const minimumExtraMiles = Number(standardExtraMiles.toFixed(2));

    return {
      standardRate,
      standardExtraMiles,
      extendedRate,
      extendedExtraMiles,
      minimumRate,
      minimumExtraMiles,
    };
  };

  useEffect(() => {
    fetchPricing();
  }, []);
  return (
    <div className="divider">
      <div className="container lg:col-span-3">
        <Heading
          title="Pricing"
          text="Choose the vehicle selector below to preview pricing and make changes on the right-side panel (all percentages are in decimal format)."
        ></Heading>

        <div className="info lg:grid-cols-2">
          {loading ? (
            <div className="grid">
              <p className="text-sm text-stone-400">Vehicle Selector:</p>
              <div className="w-full h-8 rounded-md bg-stone-600 animate-pulse"></div>
            </div>
          ) : (
            <VehicleSelector
              onSelect={selectVehicle}
              label="Vehicle Selector"
            />
          )}
        </div>

        <Heading title="Pricing Preview" isBackArrow={false} onClick={true}>
          <MdClose onClick={() => setVehicles([])} className="icon" />
        </Heading>
        {loading ? (
          <LoadingCard max={3} columns={7} />
        ) : vehicles.length > 0 ? (
          <div className="list">
            <div className="labels lg:grid-cols-9">
              <p>Vehicle</p>
              <p className="lg:col-span-2"></p>
              <p className="col-span-2 text-emerald-500">Standard Package</p>
              <p className="col-span-2 text-sky-500">Extended Package</p>
              <p className="col-span-2 text-amber-400">Minimum Package</p>
            </div>
            {vehicles.map((vehicle, idx) => (
              <div className="grid grid-cols-9 pill" key={idx}>
                <img
                  src={vehicle.cornerImage}
                  className="object-contain w-full"
                />

                <div className="lg:col-span-2">
                  <p className="text-xs font-semibold text-stone-400">
                    {vehicle.year}
                  </p>
                  <p className="font-bold">
                    {vehicle.make.name} {vehicle.model}
                  </p>
                  <p className="text-xs text-stone-400">{vehicle.id}</p>
                  <p className="text-xs">
                    {formatCurrency(vehicle.application?.highValue)} - {formatCurrency(vehicle.application?.monthlyPayment)}/mo
                  </p>
                </div>

                <div className="grid gap-1">
                  <div>
                    <p className="text-xs font-semibold text-stone-400">
                      Current
                    </p>
                    <p className="text-sm text-stone-200">
                      {formatCurrency(vehicle.standardRate) || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-400">New</p>
                    <p className="text-emerald-500">
                      {formatCurrency(vehicle.newStandardRate)}
                    </p>
                  </div>
                </div>

                <div className="grid gap-1">
                  <div>
                    <p className="text-xs font-semibold text-stone-400">
                      Current
                    </p>
                    <p className="text-sm text-stone-200">
                      {formatCurrency(vehicle.standardExtraMiles) || "-"}/mi
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-400">New</p>
                    <p className="text-emerald-500">
                      {formatCurrency(vehicle.newStandardExtraMiles)}/mi
                    </p>
                  </div>
                </div>

                <div className="grid gap-1">
                  <div>
                    <p className="text-xs font-semibold text-stone-400">
                      Current
                    </p>
                    <p className="text-sm text-stone-200">
                      {formatCurrency(vehicle.extendedRate) || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-400">New</p>
                    <p className="text-sky-500">
                      {formatCurrency(vehicle.newExtendedRate)}
                    </p>
                  </div>
                </div>

                <div className="grid gap-1">
                  <div>
                    <p className="text-xs font-semibold text-stone-400">
                      Current
                    </p>
                    <p className="text-sm text-stone-200">
                      {formatCurrency(vehicle.extendedExtraMiles) || "-"}/mi
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-400">New</p>
                    <p className="text-sky-500">
                      {formatCurrency(vehicle.newExtendedExtraMiles)}/mi
                    </p>
                  </div>
                </div>

                <div className="grid gap-1">
                  <div>
                    <p className="text-xs font-semibold text-stone-400">
                      Current
                    </p>
                    <p className="text-sm text-stone-200">
                      {formatCurrency(vehicle.minimumRate) || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-400">New</p>
                    <p className="text-amber-400">
                      {formatCurrency(vehicle.newMinimumRate)}
                    </p>
                  </div>
                </div>

                <div className="grid gap-2">
                  <div>
                    <p className="text-xs font-semibold text-stone-400">
                      Current
                    </p>
                    <p className="text-sm text-stone-200">
                      {formatCurrency(vehicle.minimumExtraMiles) || "-"}/mi
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-400">New</p>
                    <p className="text-amber-400">
                      {formatCurrency(vehicle.newMinimumExtraMiles)}/mi
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full text-center">
            <p className="font-semibold text-stone-200">
              No Vehicles Selected!
            </p>
            <p className="text-sm text-stone-400">
              Please use the vehicle selector above to preview prices for
              vehicles.
            </p>
          </div>
        )}
      </div>
      <div className="sidebar">
        <div className="grid gap-8 text-sm">
          <div className="flex items-center justify-around space-x-4">
            {edit ? (
              <Fragment>
                <MdClose onClick={() => setEdit(false)} className="icon" />
                <MdCheck onClick={updatePricing} className="icon" />
              </Fragment>
            ) : (
              <Fragment>
                <MdEdit onClick={() => setEdit(true)} className="icon" />
                <MdRefresh className="icon" onClick={refreshPricing} />
              </Fragment>
            )}
          </div>
          <div className="grid gap-4">
            <DetailPair
              label="APR (%)"
              loading={loading}
              edit={edit}
              value={pricing.apr}
              inputOptions={{
                id: "apr",
                type: "number",
                step: "0.001",
                defaultValue: pricing.apr,
              }}
            />
            <DetailPair
              label="Term (months)"
              value={pricing.term}
              loading={loading}
              edit={edit}
              inputOptions={{
                id: "term",
                type: "number",
                step: "1",
                defaultValue: pricing.term,
              }}
            />
            <DetailPair
              label="Tax (%)"
              value={pricing.taxes}
              loading={loading}
              edit={edit}
              inputOptions={{
                id: "taxes",
                type: "number",
                step: "0.001",
                defaultValue: pricing.taxes,
              }}
            />
            {loading ? (
              <div className="grid w-3/4 gap-1">
                <div className="w-1/3 h-4 rounded animate-pulse bg-stone-700"></div>
                <div className="h-6 rounded animate-pulse bg-stone-600"></div>
              </div>
            ) : (
              <div className="text-sm">
                <p className="text-stone-400">
                  Depreciation Year 1 - Year 6 (%):
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3, 4, 5, 6].map((year) => (
                    <input
                      type="number"
                      step="0.001"
                      className="form-input"
                      id={`depreciation_${year}`}
                      key={year}
                      defaultValue={
                        pricing.depreciation
                          ? pricing.depreciation[year - 1]
                          : 0
                      }
                      disabled={!edit}
                    />
                  ))}
                </div>
              </div>
            )}

            <DetailPair
              label="Standard Miles (mi)"
              value={pricing.standardMiles}
              loading={loading}
              edit={edit}
              inputOptions={{
                id: "standardMiles",
                type: "number",
                step: "1",
                defaultValue: pricing.standardMiles,
              }}
            />
            <DetailPair
              label="Extended Miles (mi)"
              value={pricing.extendedMiles}
              loading={loading}
              edit={edit}
              inputOptions={{
                id: "extendedMiles",
                type: "number",
                step: "1",
                defaultValue: pricing.extendedMiles,
              }}
            />
            <DetailPair
              label="Minimum Miles (mi)"
              value={pricing.minimumMiles}
              loading={loading}
              edit={edit}
              inputOptions={{
                id: "minimumMiles",
                type: "number",
                step: "1",
                defaultValue: pricing.minimumMiles,
              }}
            />
            <DetailPair
              label="Current Year"
              value={pricing.currentYear || new Date().getFullYear()}
              loading={loading}
              edit={edit}
              inputOptions={{
                id: "currentYear",
                type: "number",
                step: "1",
                defaultValue: pricing.currentYear || new Date().getFullYear(),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
